import { PERMISSIONS, useAuthStore } from "@/lib";
import { ScholarshipTitle, scholarshipTitleApi, ScholarshipTitleFilters } from "@/lib/api/scholarship-title";
import { useCallback, useEffect, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { notify } from "@/lib/utils/toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { PaginationIndex } from "./PaginationIndex";
import { Button } from "./ui/button";
import { ChevronDown, Edit, Loader2, Plus, RefreshCw, Search, Trash2 } from "lucide-react";
import { NgocInput } from "./ui/ngocInput";
import { ScholarshipTitlteModal } from "./modals/ScholarshipTitlteModal";
import { format } from "date-fns";
import { AccessDenied } from "./AccessDenied";
import { SmoothLoadingWrapper } from "./animate/SmoothLoadingWrapper";

export default function ScholarshipTitleManagement() {
    const { hasPermission } = useAuthStore();
    const [scholarshipTitles, setScholarshipTitles] = useState<ScholarshipTitle[]>([]);
    const [isFiltering, setIsFiltering] = useState(false);
    const [loading, setLoading] = useState(true);
    const [autoFilters, setAutoFilters] = useState<ScholarshipTitleFilters>({});
    const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [modalMode, setModalMode] = useState<"create" | "edit">("create");

    // Modal states
    const [isHandleModalOpen, setHandleModalOpen] = useState(false);
    const [editingScholarshipTitle, setEditingScholarshipTitle] = useState<ScholarshipTitle | null>(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Permission checks
    const canView = hasPermission(PERMISSIONS.scholarshipTitles.permissions.view.key);
    const canCreate = hasPermission(PERMISSIONS.scholarshipTitles.permissions.create.key);
    const canUpdate = hasPermission(PERMISSIONS.scholarshipTitles.permissions.update.key);
    const canDelete = hasPermission(PERMISSIONS.scholarshipTitles.permissions.delete.key);

    if (!canView) return <AccessDenied name='danh sách danh hiệu' />;

    const fetchList = useCallback(async (customFilters?: ScholarshipTitleFilters) => {
        let finalFilter = customFilters || autoFilters;
        try {
            setLoading(true);

            // Check if API is available
            if (!scholarshipTitleApi || typeof scholarshipTitleApi.getAll !== "function") {
                throw new Error("API service not available");
            }

            const result = await scholarshipTitleApi.getAll(finalFilter);

            if (result && result.success && result.data) {
                setScholarshipTitles(result.data.data || []);
                setTotalCount(result.data.total || 0);
                setTotalPages(result.data.totalPages || 1);
            } else {
                console.warn("API returned unsuccessful result:", result);
                // Set empty data as fallback
                setScholarshipTitles([]);
                setTotalCount(0);
                setTotalPages(1);
            }
        } catch (error) {
            console.error("Error fetching degrees:", error);
            // Set empty data as fallback
            setScholarshipTitles([]);
            setTotalCount(0);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    }, [autoFilters]);

    useEffect(() => {
        fetchList({
            ...autoFilters,
            page: currentPage,
        });
    }, [currentPage]);

    useEffect(() => {
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
    }, [itemsPerPage]);

    const startFilters = () => {
        setIsFiltering(true);
        fetchList();
    };

    const clearFilters = () => {
        setSearchTerm("");
        setIsFiltering(false);
        setCurrentPage(1);
        setItemsPerPage(10);
        setAutoFilters({});
        fetchList({});
    };

    useEffect(() => {
        setAutoFilters({
            search: searchTerm.trim().toLocaleLowerCase(),
            page: 1,
            limit: itemsPerPage,
        })
    }, [itemsPerPage, searchTerm]);


    const handleAdd = async (newDegree: Omit<ScholarshipTitle, "id" | "createdAt" | "updatedAt">) => {
        if (!canCreate) {
            notify.error("Bạn không có quyền tạo danh hiệu");
            return;
        }

        try {
            const result = await scholarshipTitleApi.create(newDegree);

            if (result.success) {
                await fetchList();
                notify.success("Thêm danh hiệu thành công");
                setHandleModalOpen(false);
            } else {
                throw new Error(result.message || "Lỗi khi thêm danh hiệu");
            }
        } catch (error) {
            console.error("Error creating scholarship title:", error);
            const message = error instanceof Error ? error.message : "Lỗi khi thêm danh hiệu";
            notify.error(message);
        } finally {
        }
    };

    const handleEdit = (title: ScholarshipTitle) => {
        if (!canUpdate) {
            notify.error("Bạn không có quyền chỉnh sửa danh hiệu");
            return;
        }
        setEditingScholarshipTitle(title);
    };

    const handleDelete = async () => {
        if (!canDelete) {
            notify.error("Bạn không có quyền xóa danh hiệu");
            return;
        }
        if (deleteItemId == null) {
            notify.error("Chưa chọn danh hiệu cần xóa");
            return;
        }

        try {
            setIsDeleting(true);
            const result = await scholarshipTitleApi.delete(deleteItemId);

            if (result.success) {
                await fetchList();
                notify.success("Xóa danh hiệu thành công");
            } else {
                throw new Error("Lỗi khi xóa danh hiệu");
            }
        } catch (error) {
            console.error("Error deleting scholarship title:", error);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleUpdate = async (updatedTitle: ScholarshipTitle) => {
        if (!canUpdate) {
            notify.error("Bạn không có quyền cập nhật danh hiệu");
            return;
        }

        try {
            setIsUpdating(true);
            const result = await scholarshipTitleApi.update(updatedTitle.id, updatedTitle);

            if (result.success) {
                await fetchList();
                notify.success("Cập nhật danh hiệu thành công");
            } else {
                throw new Error("Lỗi khi cập nhật danh hiệu");
            }
        } catch (error) {
            console.error("Error updating scholarship title:", error);
        } finally {
            setIsUpdating(false);
        }
    };

    return <div className='space-y-6'>
        <div className='flex items-center justify-between'>
            <div>
                <h2 className='text-2xl mb-2'>Quản Lý Danh Hiệu Khen Thưởng - Chứng Nhận</h2>
            </div>
            <div className='flex gap-2'>
                <Button
                    variant='outline'
                    onClick={() => fetchList()}
                    disabled={loading}
                >
                    <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                    Làm mới
                </Button>
                {canCreate && (
                    <Button
                        variant={"add"}
                        onClick={() => {
                            setModalMode("create");
                            setHandleModalOpen(true);
                        }}
                    >
                        <Plus className='w-4 h-4' />
                        Thêm danh hiệu
                    </Button>
                )}
            </div>
        </div>

        <Card>
            <CardHeader>
                <CardTitle>Danh Sách Danh Hiệu Khen Thưởng - Chứng Nhận</CardTitle>
                <div className='flex flex-wrap items-center gap-4 mt-4'>
                    <div className='relative'>
                        <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                        <NgocInput placeholder='Tìm kiếm theo tên danh hiệu' value={searchTerm} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setSearchTerm(e.target.value)} maxLength={100} className="pl-8 min-w-96" />
                    </div>

                    <Button size={"sm"} variant='edit' onClick={() => { startFilters() }}>
                        Lọc
                    </Button>
                    <Button size={"sm"} variant='outline' onClick={clearFilters}>
                        Đặt lại
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                {/* Table with horizontal scroll */}
                <Select value={itemsPerPage.toString()} onValueChange={(value) => {
                    setItemsPerPage(Number(value));
                    fetchList({
                        ...autoFilters,
                        limit: Number(value),
                        page: 1
                    });
                }}>
                    <SelectTrigger className='w-20'>
                        <SelectValue placeholder='Hình thức đào tạo' />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value='10'>10</SelectItem>
                        <SelectItem value='15'>15</SelectItem>
                        <SelectItem value='20'>20</SelectItem>
                        <SelectItem value='50'>50</SelectItem>
                    </SelectContent>
                </Select>
                <div className='relative rounded-md border mt-2'>
                    <SmoothLoadingWrapper loading={loading}>
                        <Table className='min-w-[1200px]'>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className='w-[50px] text-center'>STT</TableHead>
                                    <TableHead>Tên danh hiệu</TableHead>
                                    <TableHead>Ghi chú</TableHead>
                                    <TableHead className='w-[200px]'>Ngày tạo</TableHead>
                                    <TableHead className='w-[200px]'>Ngày cập nhật</TableHead>
                                    <TableHead className='w-[180px]'>Thao tác</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {scholarshipTitles.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className='text-center py-8 text-muted-foreground'>
                                            {searchTerm ? "Không tìm thấy kết quả phù hợp" : "Không có dữ liệu"}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    scholarshipTitles.map((degree, order) => (
                                        <TableRow key={degree.id}>
                                            <TableCell className="whitespace-normal text-sm text-center" style={{ wordBreak: 'break-word' }}>{order + 1}</TableCell>
                                            <TableCell className='whitespace-normal text-sm' style={{ wordBreak: 'break-word' }}>
                                                {degree.titleName}
                                            </TableCell>
                                            <TableCell className="whitespace-normal text-sm" style={{ wordBreak: 'break-word' }}>{degree.notes}</TableCell>
                                            <TableCell className="whitespace-normal text-sm" style={{ wordBreak: 'break-word' }}>{format(new Date(degree.createdAt), "dd/MM/yyyy HH:mm:ss")}</TableCell>
                                            <TableCell className="whitespace-normal text-sm" style={{ wordBreak: 'break-word' }}>{format(new Date(degree.updatedAt), "dd/MM/yyyy HH:mm:ss")}</TableCell>
                                            <TableCell>
                                                <div className='flex items-center gap-1'>
                                                    {canUpdate && (
                                                        <Button
                                                            variant='ghost'
                                                            onClick={() => {
                                                                setModalMode("edit");
                                                                handleEdit(degree);
                                                                setHandleModalOpen(true);
                                                            }}
                                                            className='h-8 w-8 p-0 text-yellow-600 hover:text-yellow-600'
                                                            disabled={isUpdating}
                                                        >
                                                            <Edit className='w-4 h-4' />
                                                        </Button>
                                                    )}
                                                    {canDelete && (
                                                        <Button variant='ghost' onClick={() => setDeleteItemId(degree.id)} className='h-8 w-8 p-0 text-destructive hover:text-destructive' disabled={isDeleting}>
                                                            {isDeleting ? <Loader2 className='w-4 h-4 animate-spin' /> : <Trash2 className='w-4 h-4' />}
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </SmoothLoadingWrapper>
                </div>

                {/* Pagination */}
                {!loading && totalPages > 1 && <PaginationIndex setCurrentPage={setCurrentPage} currentPage={currentPage} itemsPerPage={itemsPerPage} totalCount={totalCount} totalPages={totalPages} />}
            </CardContent>
        </Card>

        <ScholarshipTitlteModal isOpen={isHandleModalOpen} onClose={() => setHandleModalOpen(false)} onAdd={handleAdd} editData={editingScholarshipTitle} onUpdate={handleUpdate} mode={modalMode} />

        <AlertDialog open={deleteItemId !== null} onOpenChange={() => setDeleteItemId(null)}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Xác nhận xóa</AlertDialogTitle>
                    <AlertDialogDescription>Bạn có chắc chắn muốn xóa? Hành động này không thể hoàn tác.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>Hủy</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete} className='bg-destructive text-destructive-foreground hover:bg-destructive/90'>
                        Xóa
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    </div>
}